import React from 'react';
import { Clickable, Constrain, Stack } from '@moda/om';
import ChevronLeftIcon from '@moda/icons/chevron-left-20';
import ChevronRightIcon from '@moda/icons/chevron-right-20';
import isTouchDevice from 'is-touch-device';
import classNames from 'classnames';
import { useIsScrollable, useNode } from '@moda/portal-stanchions';
import { LookCellFragment } from '../../../generated/types';
import { LooksCarousel } from '../LooksCarousel';
import { LooksCarouselItem } from '../LooksCarouselItem';
import { LooksScrollStateSwitcher } from '../LooksStateSwitcher/LooksScrollStateSwitcher';
import { LooksThumbnailViewSwitcher } from '../LooksStateSwitcher/LooksThumbnailViewSwitcher';
import { LooksDesktopModuleMode, useLooksDesktopModule } from './useLooksDesktopModule';

import './LooksDesktopModule.scss';

interface Props {
  looks: LookCellFragment[];
}

export const LooksDesktopModule: React.FC<Props> = ({ looks }) => {
  const { ref, node } = useNode();
  const { isScrollable, scrollLeft, scrollRight, isMaxLeft, isMaxRight } = useIsScrollable(node);
  const { mode, changeMode, selectedLook } = useLooksDesktopModule();

  return (
    <Constrain
      className={classNames('LooksDesktopModule', {
        'LooksDesktopModule--scrollable': isScrollable
      })}
    >
      <Stack space={2}>
        <div className="LooksDesktopModule__modes">
          <Clickable
            onClick={() => changeMode(LooksDesktopModuleMode.Large)}
            data-testid="LooksDesktopModuleMode__large"
          >
            <LooksScrollStateSwitcher selected={mode === 'large'} />
          </Clickable>

          <Clickable
            onClick={() => changeMode(LooksDesktopModuleMode.Small)}
            data-testid="LooksDesktopModuleMode__small"
          >
            <LooksThumbnailViewSwitcher selected={mode === 'small'} />
          </Clickable>
        </div>

        {isScrollable && selectedLook === undefined && (
          <Clickable
            className={classNames('LooksDesktopModule__arrow', 'LooksDesktopModule__arrow--left', {
              'LooksDesktopModule__arrow--disabled': isMaxLeft,
              'LooksDesktopModule__arrow--hidden': isTouchDevice()
            })}
            disabled={isMaxLeft}
            onClick={scrollLeft}
            onKeyDown={scrollLeft}
            tabIndex={isMaxRight ? -1 : 0}
            data-testid="LooksDesktopModule__arrow--left"
            aria-label="Previous"
          >
            <ChevronLeftIcon className="LooksDesktopModule__icon" height="24px" width="24px" />
          </Clickable>
        )}

        <LooksCarousel ref={ref}>
          {looks.map(look => (
            <LooksCarouselItem key={look.id} look={look} />
          ))}
        </LooksCarousel>

        {isScrollable && selectedLook === undefined && (
          <Clickable
            className={classNames('LooksDesktopModule__arrow', 'LooksDesktopModule__arrow--right', {
              'LooksDesktopModule__arrow--disabled': isMaxRight,
              'LooksDesktopModule__arrow--hidden': isTouchDevice()
            })}
            disabled={isMaxRight}
            onClick={scrollRight}
            onKeyDown={scrollRight}
            tabIndex={isMaxRight ? -1 : 0}
            data-testid="LooksDesktopModule__arrow--right"
            aria-label="Next"
          >
            <ChevronRightIcon className="LooksDesktopModule__icon" height="24px" width="24px" />
          </Clickable>
        )}
      </Stack>
    </Constrain>
  );
};
